/**
 * VendorDocuments.jsx — Iter 50.  Vendor-side document vault (incorporation,
 * GST, rate card, etc.).  Same business logic as the previous monolithic
 * VendorDashboard, just isolated as a sub-screen.
 */
import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { UploadSimple, Trash, X, CaretDown } from '@phosphor-icons/react';

const KIND_OPTIONS = ['incorporation', 'gst', 'pan', 'msa', 'rate_card', 'other'];

const VendorDocuments = ({ vendor, accent, onReload }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const remove = async (d) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/vendor/me/documents/${d.id}`);
      toast.success('Removed');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="vendor-documents-screen">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>Compliance</div>
        <h1 className="text-3xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>Documents</h1>
        <p className="text-sm text-text-secondary">Upload incorporation, GST, PAN, MSA, and rate-card docs so we can verify you faster and surface them to founders during onboarding.</p>
      </div>

      <div className="bg-surface border border-border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[11px] uppercase tracking-[0.18em] font-bold text-text-muted">Uploaded ({(vendor.documents || []).length})</h2>
          <button onClick={() => setModalOpen(true)} data-testid="vendor-doc-add"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1" style={{ backgroundColor: accent }}>
            <UploadSimple size={10} weight="bold" /> Upload
          </button>
        </div>
        {(vendor.documents || []).length === 0 ? (
          <p className="text-sm text-text-muted">Upload your incorporation cert, GST cert, and rate card so we can verify you faster.</p>
        ) : (
          <div className="space-y-2">
            {vendor.documents.map((d) => (
              <div key={d.id} className="bg-background border border-border rounded-lg px-3 py-2 flex justify-between items-center" data-testid={`vendor-doc-${d.id}`}>
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded mr-2" style={{ backgroundColor: `${accent}1a`, color: accent }}>{d.kind}</span>
                  <span className="text-sm">{d.title}</span>
                </div>
                <button onClick={() => remove(d)} className="text-text-muted hover:text-danger" aria-label="Delete">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && <DocModal accent={accent} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); onReload(); }} />}
    </div>
  );
};

const DocModal = ({ accent, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: '', kind: 'incorporation', file_id: null });
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/files/upload', fd);
      setForm((p) => ({ ...p, file_id: r.data.file_id })); setFilename(file.name);
    } catch (e) { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.file_id) { toast.error('Title + file required'); return; }
    setBusy(true);
    try {
      await api.post('/vendor/me/documents', form);
      toast.success('Uploaded');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4" onClick={onClose} data-testid="vendor-doc-modal">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Upload document</h3>
          <button onClick={onClose}><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Type</label>
            <div className="relative">
              <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}
                data-testid="vendor-doc-kind"
                className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-7 py-2 text-sm">
                {KIND_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <CaretDown size={9} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Title *</label>
            <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              data-testid="vendor-doc-title"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">File *</label>
            <input type="file" onChange={(e) => upload(e.target.files?.[0])} data-testid="vendor-doc-file" className="text-xs" />
            {filename && <div className="text-xs text-text-secondary mt-1">{filename}</div>}
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="vendor-doc-save"
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: accent }}>
            {busy ? 'Uploading…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorDocuments;
